import { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { OriginalPrice } from './original-price';
import { SalePrice } from './sale-price';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { PriceTypes } from '@/models/catalog/course';
import type { CheapestOffer, Installment } from '@/models/catalog/offer';

const getHighestInstallment = (installments: Installment[]): Installment => {
  return installments?.reduce((acc, curr) => {
    return acc.max > curr.max ? acc : curr;
  });
};

const getHighestInstallmentWithoutFee = (
  installments: Installment[]
): Installment => {
  return installments?.reduce((acc, curr) => {
    return acc.max > curr.max && curr.fee === 0 ? curr : acc;
  });
};

const PriceUponRequest = () => {
  return (
    <Box mt={1}>
      <Alert icon={false} severity="info">
        O preço será apresentado pela instituição no momento da matrícula na
        mesma. Para saber mais,{' '}
        <Link href="https://atendimento.galena.com/hc/pt-br/articles/23938037836941-O-que-significa-valor-sob-consulta-na-plataforma-Galena">
          clique aqui
        </Link>
        .
      </Alert>
    </Box>
  );
};

interface PricesProps {
  isFullIncluded: boolean;
  pricing: CheapestOffer['pricing_description'];
  isOfferFiltersLoading: boolean;
  isSingleOffer: boolean;
}

export const Prices = ({
  isFullIncluded,
  pricing,
  isOfferFiltersLoading,
  isSingleOffer,
}: PricesProps) => {
  const isIncluded = isFullIncluded && pricing?.discount_percentage === 100;

  const saleDiscountPercentage = pricing?.discount_percentage || 0;
  const salePrice = pricing?.sale_price ?? null;
  const originalPrice = pricing?.original_price ?? null;
  const type = pricing?.type ?? PriceTypes.UponRequest;
  const paymentConditions = pricing?.payment_conditions;

  const hasKnownPrice = useMemo(() => {
    return !!(salePrice && /\d+/.test(`${salePrice}`));
  }, [salePrice]);

  const installment = paymentConditions?.installments
    ? getHighestInstallment(paymentConditions.installments)
    : null;

  const installmentWithoutFee = paymentConditions?.installments
    ? getHighestInstallmentWithoutFee(paymentConditions.installments)
    : null;

  const formattedDiscount = () => {
    const discountLabel =
      saleDiscountPercentage === 100 || isSingleOffer
        ? Math.floor(saleDiscountPercentage)
        : `até ${Math.floor(saleDiscountPercentage)}`;

    return `${discountLabel}% OFF`;
  };

  const fullDiscount = saleDiscountPercentage === 100;

  if (isOfferFiltersLoading) return <Skeleton height={90} variant="rounded" />;

  return (
    <Box mt="auto">
      <Box display="flex" alignItems="start">
        <Stack spacing={1} flex={1}>
          {hasKnownPrice && !isSingleOffer && (
            <Typography component="div">A partir de</Typography>
          )}
          {originalPrice !== salePrice && (
            <OriginalPrice
              hasKnownPrice={hasKnownPrice}
              originalPrice={originalPrice}
              type={type}
              installment={installment}
            />
          )}
          <SalePrice
            salePrice={salePrice}
            isIncluded={isIncluded ?? false}
            installment={installment}
            installmentWithoutFee={installmentWithoutFee}
            type={type}
          />
        </Stack>
        {saleDiscountPercentage > 0 && (
          <Chip
            label={formattedDiscount()}
            variant={fullDiscount ? 'filled' : 'outlined'}
            color="success"
            sx={{ border: 'none', fontWeight: fontWeights.fontWeightSemibold }}
            size="small"
          />
        )}
      </Box>
      {salePrice === null && <PriceUponRequest />}
    </Box>
  );
};
